import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { Document as PDFDocument, Page as PDFPage } from 'react-pdf';

// BookSpreadPreview — renders PDF as side-by-side book spreads
// Uses pdf() to generate blob, react-pdf (viewer) to display pages
export default function BookSpreadPreview({ bookDoc, bookKey, previewIdx, setPreviewIdx, onBack, onSave, chapterCount, est, isHome, onRealPageCount }) {
  // Use pdf().toBlob() on main thread instead of usePDF hook (Web Worker)
  // — Web Workers have separate memory and can fail to load images on some devices
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [pdfLoading, setPdfLoading] = React.useState(true);
  const [pdfError, setPdfError] = React.useState(null);
  const prevKeyRef = React.useRef(null);

  React.useEffect(() => {
    if (!bookDoc) return;
    // Only regenerate PDF when bookKey changes (not on every render)
    if (bookKey && prevKeyRef.current === bookKey) return;
    prevKeyRef.current = bookKey || null;
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);
    (async () => {
      try {
        const blob = await pdf(bookDoc).toBlob();
        if (cancelled) return;
        setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
      } catch (err) {
        console.error('[BookPreview] PDF generation error:', err);
        if (!cancelled) setPdfError('Something went wrong generating the preview. Please try again.');
      }
      if (!cancelled) setPdfLoading(false);
    })();
    return () => { cancelled = true; prevKeyRef.current = null; };
  }, [bookKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const instance = { url: pdfUrl, loading: pdfLoading, error: pdfError };
  const [numPages, setNumPages] = React.useState(null);
  const reportedPageCountRef = React.useRef(null);
  React.useEffect(() => {
    if (numPages && numPages !== reportedPageCountRef.current && onRealPageCount) {
      reportedPageCountRef.current = numPages;
      onRealPageCount(numPages);
    }
  }, [numPages]); // eslint-disable-line react-hooks/exhaustive-deps
  const containerRef = React.useRef(null);
  const [pageWidth, setPageWidth] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const calcSize = () => {
      const container = containerRef.current;
      if (!container) return;
      const containerW = container.offsetWidth;
      const viewportH = window.innerHeight;
      const mobile = containerW < 500;
      setIsMobile(mobile);

      if (mobile) {
        const pw = containerW - 24;
        const maxFromH = (viewportH - 140) * (8.5 / 11);
        setPageWidth(Math.max(260, Math.min(pw, maxFromH)));
      } else {
        const maxPageW = (containerW - 30 - 3) / 2;
        const overhead = isHome ? 140 : 160;
        const maxPageH = viewportH - overhead;
        const fromHeight = maxPageH * (8.5 / 11); // convert max height to width using 8.5:11 ratio
        const pw = Math.min(maxPageW, fromHeight);
        setPageWidth(Math.max(280, Math.floor(pw)));
      }
    };
    calcSize();
    window.addEventListener('resize', calcSize);
    return () => window.removeEventListener('resize', calcSize);
  }, [isHome]);

  const pageHeight = pageWidth ? pageWidth / (8.5 / 11) : 0; // 8.5×11 portrait ratio
  const maxPage = numPages || 9999;
  const totalSpreads = numPages ? Math.ceil(numPages / 2) : 1;
  const totalPages = numPages || 1;

  const getSpreadPages = (si) => {
    const left = si * 2 + 1;
    const right = si * 2 + 2;
    return [left <= maxPage ? left : null, right <= maxPage ? right : null];
  };

  const [leftPage, rightPage] = isMobile ? [null, null] : getSpreadPages(previewIdx);
  const mobilePageNum = previewIdx + 1;

  const atEnd = isMobile ? previewIdx >= totalPages - 1 : previewIdx >= totalSpreads - 1;
  const prev = () => setPreviewIdx(Math.max(0, previewIdx - 1));
  const next = () => setPreviewIdx(Math.min(isMobile ? totalPages - 1 : totalSpreads - 1, previewIdx + 1));

  const isReady = !instance.loading && !instance.error && pageWidth;

  return (
    <div ref={containerRef} style={{ textAlign: 'center', padding: isHome ? '0' : '16px 16px 24px', position: 'relative' }}>
      {!isHome && onBack && <button onClick={onBack} className="st-back" style={{ marginBottom: 12 }}>← Back to My Book</button>}

      {instance.error ? (
        <div style={{ textAlign: 'center', padding: '40px 16px' }}>
          <p style={{ color: '#c0392b' }}>Error generating preview. Please try again.</p>
        </div>
      ) : !isReady ? (
        <div style={{ textAlign: 'center', padding: isHome ? '40px 16px' : '60px 16px' }}>
          <div style={{ fontSize: '1.1rem', color: '#3c4653', marginBottom: 12 }}>Generating your book...</div>
          <div style={{ width: 200, height: 4, background: 'rgba(15,23,42,0.07)', borderRadius: 2, margin: '0 auto', overflow: 'hidden' }}>
            <div style={{ width: '60%', height: '100%', background: 'rgba(15,23,42,0.2)', borderRadius: 2, animation: 'loading 1.5s ease-in-out infinite' }} />
          </div>
        </div>
      ) : (
      <>
      <div style={{
        width: '100%', margin: '0 auto 8px',
        borderRadius: 8, overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 8px 40px rgba(0,0,0,0.08)',
        background: '#e8e0d4',
        padding: isMobile ? '8px 4px' : (isHome ? '12px 6px' : '16px 8px'),
      }}>
        {isMobile ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: pageHeight }}>
            <div style={{ background: '#fbf8f1', borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
              {mobilePageNum <= maxPage && instance.url ? (
                <PDFDocument file={instance.url} onLoadSuccess={({ numPages: n }) => setNumPages(n)} loading="">
                  <PDFPage pageNumber={mobilePageNum} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} loading="" />
                </PDFDocument>
              ) : (
                <div style={{ width: pageWidth, height: pageHeight, background: '#fbf8f1' }} />
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch', gap: 2, minHeight: pageHeight }}>
            <div style={{ flex: '0 0 auto', background: '#fbf8f1', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: '4px 0 0 4px' }}>
              {leftPage && instance.url ? (
                <PDFDocument file={instance.url} onLoadSuccess={({ numPages: n }) => setNumPages(n)} loading="">
                  <PDFPage pageNumber={leftPage} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} loading="" />
                </PDFDocument>
              ) : (
                <div style={{ width: pageWidth, height: pageHeight, background: '#fbf8f1' }} />
              )}
            </div>
            <div style={{ width: 3, background: 'linear-gradient(to right, rgba(0,0,0,0.12), rgba(0,0,0,0.03), rgba(0,0,0,0.12))', flexShrink: 0 }} />
            <div style={{ flex: '0 0 auto', background: '#fbf8f1', boxShadow: 'inset 4px 0 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: '0 4px 4px 0' }}>
              {rightPage && instance.url ? (
                <PDFDocument file={instance.url} onLoadSuccess={({ numPages: n }) => setNumPages(n)} loading="">
                  <PDFPage pageNumber={rightPage} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} loading="" />
                </PDFDocument>
              ) : (
                <div style={{ width: pageWidth, height: pageHeight, background: '#fbf8f1' }} />
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 8 : 12, marginBottom: 4 }}>
        <button onClick={prev} disabled={previewIdx === 0} className="st-btn-secondary"
          style={{ padding: isMobile ? '8px 14px' : '8px 16px', fontSize: '1.05rem', opacity: previewIdx === 0 ? 0.3 : 1 }}>←</button>
        <span style={{ fontSize: isMobile ? '0.85rem' : '0.9rem', color: '#3c4653', fontFamily: "'Lora', serif", display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="text"
            inputMode="numeric"
            value={previewIdx + 1}
            onChange={e => {
              const val = parseInt(e.target.value, 10);
              const maxVal = isMobile ? totalPages : totalSpreads;
              if (!isNaN(val) && val >= 1 && val <= maxVal) setPreviewIdx(val - 1);
            }}
            onFocus={e => e.target.select()}
            style={{
              width: 36, textAlign: 'center', border: '1px solid rgba(15,23,42,0.15)',
              borderRadius: 6, padding: '4px 2px', fontSize: 'inherit', color: '#0f172a',
              fontFamily: "'Lora', serif", background: '#fbf8f1', outline: 'none',
            }}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          />
          <span>/ {isMobile ? totalPages : totalSpreads}</span>
        </span>
        <button onClick={next} disabled={atEnd} className="st-btn-secondary"
          style={{ padding: isMobile ? '8px 14px' : '8px 16px', fontSize: '1.05rem', opacity: atEnd ? 0.3 : 1 }}>→</button>
      </div>
      {/* Page slider for quick navigation */}
      {isReady && <div style={{ maxWidth: 600, margin: '4px auto 16px', padding: '0 16px' }}>
        <input type="range" className="page-slider" min={0}
          max={isMobile ? Math.max(0, totalPages - 1) : Math.max(0, totalSpreads - 1)}
          value={previewIdx}
          onChange={e => setPreviewIdx(Number(e.target.value))}
        />
      </div>}

      {!isHome && (
        <>
          <p style={{ fontSize: '1.05rem', color: '#3c4653', marginBottom: 16, fontStyle: 'italic' }}>
            {chapterCount} chapters · {numPages || '...'} pages
          </p>
          {onSave && <button onClick={onSave} className="st-btn-primary" style={{ width: '100%', marginBottom: 12 }}>💾 Save My Book</button>}
          <p style={{ fontSize: '1rem', color: '#3c4653', textAlign: 'center' }}>
            {numPages || '~' + est.printPages} printed pages · est. ${est.price} · Ordering coming soon
          </p>
        </>
      )}
      </>
      )}
    </div>
  );
}
